import { Injectable } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import * as moment from 'moment';
import { EditorFieldType } from '../enums/editor-field-type-enum.service';
import { EditorSpecialItemType } from '../enums/editor-special-item-type-enum.service';
import { EditorColumnConfig } from '../models/editor-column-config.model';
import { EditorConfig } from '../models/editor-config.model';
import { EditorFieldConfig, EditorFieldConfigCombined } from '../models/editor-field-config.model';
import { EditorItemConfig, EditorSpecialFieldConfig } from '../models/editor-item-type';
import { EditorArrayConfig } from '../models/editor-new-array-config.model';

@Injectable({
  providedIn: 'root',
})
export class EditorHelperService {
  constructor(private formBuilder: FormBuilder) {}

  createForm(config: EditorConfig, data?: object): FormGroup {
    if (!config) {
      return this.formBuilder.group({});
    }

    const controls: Record<string, AbstractControl> = this.createControls(config, data);
    const options: AbstractControlOptions = {
      validators: config?.groupValidators || [],
      asyncValidators: config?.groupAsyncValidators || [],
    };
    return this.formBuilder.group(controls, options);
  }

  getValidators(field: EditorFieldConfigCombined): ValidatorFn[] {
    return [
      ...this.getValidatorsByType(field),
      ...(field.validators || []),
      this.getRequiredValidator(field),
      this.getMinValidator(field),
      this.getMaxValidator(field),
      field.minLength === undefined ? null : Validators.minLength(field.minLength),
      field.maxLength === undefined ? null : Validators.maxLength(field.maxLength),
    ].filter(Boolean) as ValidatorFn[];
  }

  initializeArrayControls(form: FormGroup): FormGroup {
    if (!form.get('_items')) {
      form.addControl('_items', this.formBuilder.array([]));
    }
    if (!form.get('items')) {
      form.addControl('items', this.formBuilder.control(undefined));
    }
    if (!form.get('removed')) {
      form.addControl('removed', this.formBuilder.control(undefined));
    }
    if (!form.get('added')) {
      form.addControl('added', this.formBuilder.control(undefined));
    }
    if (!form.get('updated')) {
      form.addControl('updated', this.formBuilder.control(undefined));
    }

    return form;
  }

  private createControls(config: EditorConfig, data?: object): Record<string, AbstractControl> {
    const items: EditorConfig['items'] = config?.items || [];
    const [firstItem]: EditorConfig['items'] = items;

    let formItems: EditorItemConfig[];
    if (firstItem && firstItem.type === EditorSpecialItemType.Column) {
      formItems = (items as EditorColumnConfig[]).flatMap((column: EditorColumnConfig) => column.items);
    } else {
      formItems = items as EditorItemConfig[];
    }

    return formItems
      .map((item: EditorItemConfig) => item as EditorFieldConfig | EditorSpecialFieldConfig)
      .filter((item: EditorFieldConfig | EditorSpecialFieldConfig) => Boolean(item.name))
      .reduce((result: Record<string, AbstractControl>, item: EditorFieldConfig | EditorSpecialFieldConfig) => {
        result[item.name] = this.createControl(
          item as EditorFieldConfigCombined,
          data ? data[item.name as keyof object] : undefined,
        );
        return result;
      }, {});
  }

  private createControl(config: EditorFieldConfigCombined | EditorSpecialFieldConfig, data: object): AbstractControl {
    if (config.type === EditorSpecialItemType.Editor) {
      return this.createForm(config.editor, data);
    }
    if (config.type === EditorSpecialItemType.Array) {
      return this.createArrayForm(config, data);
    }
    const value: object = data ? data : config.defaultValue;

    return this.formBuilder.control(value, this.getValidators(config), this.getAsyncValidators(config));
  }

  private createArrayForm(config: EditorArrayConfig, data: object): FormGroup {
    const result: FormGroup = this.initializeArrayControls(this.formBuilder.group({}));

    if (data) {
      result.reset(data);
    }

    return result;
  }

  private getAsyncValidators(field: EditorFieldConfigCombined | EditorSpecialFieldConfig): AsyncValidatorFn[] {
    return (field as EditorFieldConfigCombined).asyncValidators || [];
  }

  private getMinValidator(field: EditorFieldConfigCombined): ValidatorFn | null {
    if (field.min === undefined) {
      return null;
    }

    if ([EditorFieldType.DATE, EditorFieldType.DATE_RANGE].includes(field.type) && typeof field.min === 'string') {
      return this.dateMinValidator(field.min);
    }
    if (typeof field.min === 'number') {
      return Validators.min(field.min);
    }

    return null;
  }

  private getMaxValidator(field: EditorFieldConfigCombined): ValidatorFn | null {
    if (field.max === undefined) {
      return null;
    }

    if ([EditorFieldType.DATE, EditorFieldType.DATE_RANGE].includes(field.type) && typeof field.max === 'string') {
      return this.dateMaxValidator(field.max);
    }
    if (typeof field.max === 'number') {
      return Validators.max(field.max);
    }

    return null;
  }

  private getRequiredValidator(field: EditorFieldConfigCombined): ValidatorFn | null {
    if (!field.required) {
      return null;
    }

    let type: EditorFieldType = field.type;
    if (type === EditorFieldType.TEMPLATE) {
      type = field.templateType;
    }

    switch (type) {
      case EditorFieldType.DATE_RANGE:
        return this.dateRangeRequiredValidator();
      case EditorFieldType.NUMBER:
      case EditorFieldType.PERCENT:
      case EditorFieldType.CURRENCY:
        return this.numberRequiredValidator();
      default:
        return Validators.required;
    }
  }

  private getValidatorsByType(field: EditorFieldConfigCombined): ValidatorFn[] {
    let type: EditorFieldType = field.type;
    if (type === EditorFieldType.TEMPLATE) {
      type = field.templateType;
    }

    const validators: Partial<Record<EditorFieldType, (ValidatorFn | AsyncValidatorFn)[]>> = {
      [EditorFieldType.EMAIL]: [Validators.email],
      [EditorFieldType.PHONE]: [Validators.minLength(11), Validators.maxLength(13)], // NOTE | SHOULD BE 11, CHANGE THIS LATER
      [EditorFieldType.DATE_RANGE]: [this.dateRangeValidator()],
      [EditorFieldType.COLOR]: [Validators.minLength(6), Validators.maxLength(6)],
    };
    return validators[type] || [];
  }

  private dateMinValidator(min: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control?.value || !min) {
        return null;
      }

      const value: string = Array.isArray(control.value) ? control.value[0] : control.value;
      const controlDate: moment.Moment = this.normalizeDate(value);

      if (!controlDate.isValid()) {
        return null;
      }

      const validationDate: moment.Moment = this.normalizeDate(min);
      return controlDate.isSameOrAfter(validationDate)
        ? null
        : {
            dateMinimum: {
              minimum: validationDate.format('YYYY-MM-DD'),
              actual: controlDate.format('YYYY-MM-DD'),
            },
          };
    };
  }

  private dateMaxValidator(max: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control?.value || !max) {
        return null;
      }

      const value: string = Array.isArray(control.value) ? control.value[1] : control.value;
      const controlDate: moment.Moment = this.normalizeDate(value);

      if (!controlDate.isValid()) {
        return null;
      }

      const validationDate: moment.Moment = this.normalizeDate(max);
      return controlDate.isSameOrBefore(validationDate)
        ? null
        : {
            dateMaximum: {
              maximum: validationDate.format('YYYY-MM-DD'),
              actual: controlDate.format('YYYY-MM-DD'),
            },
          };
    };
  }

  private normalizeDate(date: string): moment.Moment {
    if (typeof date === 'string') {
      date = date.replace('Z', '');
    }
    return moment(moment(date).format('YYYY-MM-DD'));
  }

  private dateRangeRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control) {
        return null;
      }

      if (!control.value || !Array.isArray(control.value) || control.value.length < 2) {
        return { required: true };
      }

      const [start, end]: string[] = control.value;
      if (!start || !end) {
        return { required: true };
      }

      return null;
    };
  }

  private dateRangeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control) {
        return null;
      }

      if (!control.value || !Array.isArray(control.value) || control.value.length < 2) {
        return null;
      }

      const [start, end]: string[] = control.value;
      if (!start || !end) {
        return null;
      }

      const startDate: moment.Moment = moment(start).startOf('day');
      if (!startDate.isValid) {
        return { message: 'Please specify valid start date.' };
      }

      const endDate: moment.Moment = moment(end).startOf('day');
      if (!endDate.isValid) {
        return { message: 'Please specify valid end date.' };
      }

      if (startDate.isAfter(endDate)) {
        return { message: 'Start date should be less than end date.' };
      }

      return null;
    };
  }

  private numberRequiredValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control) {
        return null;
      }
      const value: string | number = control.value;
      if (!value) {
        return { required: true };
      }

      return null;
    };
  }
}
